import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { doc, getDoc, updateDoc, collection, addDoc, getDocs, query, where, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase";
import BusinessInfo from "./BusinessInfo";
import About from "./About";
import Offers from "./Offers";
import Requests from "./Requests";
import Projects from "./Projects";
import Reviews from "./Reviews";
import AddOfferModal from "./AddOfferModal";
import AddRequestModal from "./AddRequestModal";
import EditOfferModal from "./EditOfferModal";
import EditRequestModal from "./EditRequestModal";
import SuccessModal from "./SuccessModal";

const Profile = () => {
  const [activeTab, setActiveTab] = useState('business');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [showAddOfferModal, setShowAddOfferModal] = useState(false);
  const [showAddRequestModal, setShowAddRequestModal] = useState(false);
  const [showEditOfferModal, setShowEditOfferModal] = useState(false);
  const [showEditRequestModal, setShowEditRequestModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newOffer, setNewOffer] = useState({
    title: '',
    description: '',
    price: '',
    location: '',
    category: 'solar-panels',
    image: 'https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=600&q=80'
  });
  const [newRequest, setNewRequest] = useState({
    title: '',
    description: '',
    budget: '',
    location: '',
    category: 'solar-panels',
    image: 'https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=600&q=80'
  });
  const { currentUser, userProfile, fetchUserProfile } = useAuth();

  // Extended user data with editable fields
  const [userData, setUserData] = useState({
    name: "",
    business: "",
    email: "",
    location: "",
    phone: "",
    businessDescription: "",
    specializations: "",
    yearsInBusiness: "",
    website: "",
    facebook: "",
    instagram: "",
    verified: false,
    userType: "seller", // Default user type
    stats: {
      activeProjects: 0,
      earnings: 0,
      reviews: 0,
      financing: 0
    }
  });

  // Load user data from Firebase
  const loadUserData = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserData(prev => ({
          ...prev,
          name: data.name || "",
          email: data.email || "",
          location: data.location || "",
          phone: data.phone || "",
          business: data.business || "",
          businessDescription: data.businessDescription || "",
          specializations: data.specializations || "",
          yearsInBusiness: data.yearsInBusiness || "",
          website: data.website || "",
          facebook: data.facebook || "",
          instagram: data.instagram || "",
          verified: data.verified || false,
          userType: data.userType || "seller",
          stats: {
            activeProjects: data.stats?.activeProjects || 0,
            earnings: data.stats?.earnings || 0,
            reviews: data.stats?.reviews || 0,
            financing: data.stats?.financing || 0
          }
        }));
        
        // Load appropriate data based on user type
        if (data.userType === 'seller') {
          loadOffers();
        } else {
          loadRequests();
        }
      }
    } catch (error) {
      console.error('Error loading user data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserData();
  }, [currentUser]);

  const handleInputChange = (field, value) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleStatsChange = (field, value) => {
    setUserData(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        [field]: parseInt(value) || 0
      }
    }));
  };

  // Load offers from Firebase
  const loadOffers = async () => {
    if (!currentUser) return;
    
    try {
      setLoadingOffers(true);
      const q = query(collection(db, 'offers'), where('sellerId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      const offersData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setOffers(offersData);
    } catch (error) {
      console.error('Error loading offers:', error);
    } finally {
      setLoadingOffers(false);
    }
  };

  // Load requests from Firebase
  const loadRequests = async () => {
    if (!currentUser) return;
    
    try {
      setLoadingRequests(true);
      const q = query(collection(db, 'requests'), where('buyerId', '==', currentUser.uid));
      const querySnapshot = await getDocs(q);
      const requestsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRequests(requestsData);
    } catch (error) {
      console.error('Error loading requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Add new offer
  const handleAddOffer = async (offerWithImages) => {
    if (!currentUser) return;
    
    try {
      setSaving(true);
      const offerData = {
        ...offerWithImages,
        sellerId: currentUser.uid,
        sellerName: userData.name || userData.business,
        type: 'Offer',
        status: 'Open',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await addDoc(collection(db, 'offers'), offerData);
      
      // Reset form and close modal
      setNewOffer({
        title: '',
        description: '',
        price: '',
        location: '',
        category: 'solar-panels',
        image: 'https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=600&q=80'
      });
      setShowAddOfferModal(false);
      
      // Reload offers
      await loadOffers();
      
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 3000);
    } catch (error) {
      console.error('Error adding offer:', error);
      alert('Error adding offer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Add new request
  const handleAddRequest = async (requestWithImages) => {
    if (!currentUser) return;
    
    try {
      setSaving(true);
      const requestData = {
        ...requestWithImages,
        buyerId: currentUser.uid,
        buyerName: userData.name || userData.business,
        type: 'Request',
        status: 'Open',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      await addDoc(collection(db, 'requests'), requestData);
      
      // Reset form and close modal
      setNewRequest({
        title: '',
        description: '',
        budget: '',
        location: '',
        category: 'solar-panels',
        image: 'https://images.unsplash.com/photo-1464983953574-0892a716854b?auto=format&fit=crop&w=600&q=80'
      });
      setShowAddRequestModal(false);
      
      // Reload requests
      await loadRequests();
      
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 3000);
    } catch (error) {
      console.error('Error adding request:', error);
      alert('Error adding request. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Delete offer
  const handleDeleteOffer = async (offerId) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    
    try {
      await deleteDoc(doc(db, 'offers', offerId));
      await loadOffers();
    } catch (error) {
      console.error('Error deleting offer:', error);
      alert('Error deleting offer. Please try again.');
    }
  };

  // Delete request
  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    
    try {
      await deleteDoc(doc(db, 'requests', requestId));
      await loadRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      alert('Error deleting request. Please try again.');
    }
  };

  // Edit offer
  const handleEditOffer = async (offerId, updatedOffer) => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'offers', offerId), {
        ...updatedOffer,
        updatedAt: new Date().toISOString()
      });
      
      setShowEditOfferModal(false);
      setSelectedOffer(null);
      
      // Reload offers
      await loadOffers();
      
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 3000);
    } catch (error) {
      console.error('Error updating offer:', error);
      alert('Error updating offer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Edit request
  const handleEditRequest = async (requestId, updatedRequest) => {
    try {
      setSaving(true);
      await updateDoc(doc(db, 'requests', requestId), {
        ...updatedRequest,
        updatedAt: new Date().toISOString()
      });
      
      setShowEditRequestModal(false);
      setSelectedRequest(null);
      
      // Reload requests
      await loadRequests();
      
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 3000);
    } catch (error) {
      console.error('Error updating request:', error);
      alert('Error updating request. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;
    
    try {
      setSaving(true);
      await updateDoc(doc(db, 'users', currentUser.uid), {
        ...userData,
        updatedAt: new Date().toISOString()
      });
      
      // Refresh user profile in AuthContext
      await fetchUserProfile(currentUser);
      
      setShowSuccessModal(true);
      setTimeout(() => setShowSuccessModal(false), 3000);
    } catch (error) {
      console.error('Error saving user data:', error);
      alert('Error saving data. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const tabs = userData.userType === 'buyer' ? [
    { id: 'about', label: 'About', icon: '👤' },
    { id: 'requests', label: 'My Requests', icon: '📋' },
    { id: 'projects', label: 'My Projects', icon: '🛠️' },
    { id: 'reviews', label: 'Reviews', icon: '⭐' },
  ] : [
    { id: 'business', label: 'About Business', icon: '🏢' },
    { id: 'offers', label: 'My Offers', icon: '📦' },
    { id: 'projects', label: 'My Projects', icon: '🛠️' },
    { id: 'reviews', label: 'Reviews', icon: '⭐' },
  ];

  // Set default active tab based on user type
  useEffect(() => {
    if (userData.userType === 'buyer' && activeTab === 'business') {
      setActiveTab('about');
    } else if (userData.userType === 'seller' && activeTab === 'about') {
      setActiveTab('business');
    }
  }, [userData.userType, activeTab]);

  return (
    <div className="bg-abiu-green-bg min-h-screen py-10 px-4">
      <main className="max-w-6xl mx-auto w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-abiu-green-dark mb-2">My Profile</h1>
              <p className="text-gray-700">Manage your business profile and track your performance</p>
            </div>
            <div className="text-right">
              <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-bold ${
                userData.userType === 'buyer' 
                  ? 'bg-blue-100 text-blue-800 border-2 border-blue-300' 
                  : 'bg-green-100 text-green-800 border-2 border-green-300'
              }`}>
                <span className="mr-2">
                  {userData.userType === 'buyer' ? '🛒' : '🏢'}
                </span>
                {userData.userType === 'buyer' ? 'BUYER' : 'SELLER'}
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mb-6 flex justify-end">
          <button 
            onClick={handleSave}
            disabled={saving}
            className="bg-abiu-green text-white px-6 py-2 rounded-lg font-semibold hover:bg-abiu-green-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-abiu-green text-white'
                    : 'text-gray-600 hover:text-abiu-green-dark hover:bg-abiu-green-light/20'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-abiu-green border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-600">Loading profile...</span>
            </div>
          ) : (
            <>
              {activeTab === 'business' && (
                <BusinessInfo userData={userData} handleInputChange={handleInputChange} />
              )}

              {activeTab === 'about' && (
                <About userData={userData} handleInputChange={handleInputChange} />
              )}

              {activeTab === 'offers' && (
                <Offers 
                  offers={offers}
                  loadingOffers={loadingOffers}
                  setShowAddOfferModal={setShowAddOfferModal}
                  handleDeleteOffer={handleDeleteOffer}
                  handleEditOffer={handleEditOffer}
                  setShowEditOfferModal={setShowEditOfferModal}
                  setSelectedOffer={setSelectedOffer}
                />
              )}

              {activeTab === 'requests' && (
                <Requests 
                  requests={requests}
                  loadingRequests={loadingRequests}
                  setShowAddRequestModal={setShowAddRequestModal}
                  handleDeleteRequest={handleDeleteRequest}
                  handleEditRequest={handleEditRequest}
                  setShowEditRequestModal={setShowEditRequestModal}
                  setSelectedRequest={setSelectedRequest}
                />
              )}

              {activeTab === 'projects' && <Projects />}

              {activeTab === 'reviews' && <Reviews />}
            </>
          )}
        </div>
      </main>

      {/* Modals */}
      <AddOfferModal 
        showAddOfferModal={showAddOfferModal}
        setShowAddOfferModal={setShowAddOfferModal}
        newOffer={newOffer}
        setNewOffer={setNewOffer}
        handleAddOffer={handleAddOffer}
        saving={saving}
      />

      <AddRequestModal 
        showAddRequestModal={showAddRequestModal}
        setShowAddRequestModal={setShowAddRequestModal}
        newRequest={newRequest}
        setNewRequest={setNewRequest}
        handleAddRequest={handleAddRequest}
        saving={saving}
      />

      <EditOfferModal 
        showEditOfferModal={showEditOfferModal}
        setShowEditOfferModal={setShowEditOfferModal}
        selectedOffer={selectedOffer}
        handleEditOffer={handleEditOffer}
        saving={saving}
      />

      <EditRequestModal 
        showEditRequestModal={showEditRequestModal}
        setShowEditRequestModal={setShowEditRequestModal}
        selectedRequest={selectedRequest}
        handleEditRequest={handleEditRequest}
        saving={saving}
      />

      <SuccessModal showSuccessModal={showSuccessModal} />
    </div>
  );
};

export default Profile; 